import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { Skeleton } from "@/shared/ui";
import { useScrollDetection, useLocale } from "@/shared/hooks";
import { AuditEntryItem } from "@/shared/components";
import type { AuditEntryItemConfig } from "@/shared/components";
import type { AuditEntry } from "@/shared/types/audit";
import { useGetRequestAuditQuery } from "../api";
import { REQUEST_AUDIT_ACTION_LABEL_MAP, REQUEST_AUDIT_ICON_MAP } from "../consts";

interface Props {
  requestType: string;
  requestId: number;
  kind?: "salary" | "employee";
}

function AuditTimelineSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} className="flex gap-3">
          <Skeleton circle width={28} height={28} />
          <div className="flex-1 flex flex-col gap-1">
            <Skeleton height={14} width={140} />
            <Skeleton height={12} width={100} />
            <Skeleton circle width={24} height={24} />
          </div>
        </div>
      ))}
    </div>
  );
}

function getSigningStatusLabelKey(entry: AuditEntry): string | undefined {
  if (!entry.action.endsWith(".cancelled")) return undefined;
  const signingStatus = entry.metadata?.signing_status;
  if (signingStatus === 4) return "audit.actions.signingRevoked";
  if (signingStatus === 9) return "audit.actions.signingRefused";
  return undefined;
}

function getEntryNote(entry: AuditEntry): string | null {
  if (entry.metadata && typeof entry.metadata === "object") {
    if ("note" in entry.metadata && entry.metadata.note) return String(entry.metadata.note);
    if ("rejection_reason" in entry.metadata && entry.metadata.rejection_reason) {
      return String(entry.metadata.rejection_reason);
    }
    if ("reason" in entry.metadata && entry.metadata.reason) return String(entry.metadata.reason);
  }
  return null;
}

function getAdvanceLabelKey(entry: AuditEntry, kind?: "salary" | "employee"): string | undefined {
  if (entry.action === "advance.created") {
    if (kind === "salary") return "audit.actions.advanceSalaryCreated";
    if (kind === "employee") return "audit.actions.advanceAccountableCreated";
    return undefined;
  }
  if (entry.action === "advance.reviewed") {
    const reviewAction = entry.changes?.review_action;
    if (reviewAction === "reject") return "audit.actions.advanceRejected";
    if (reviewAction === "revision") return "audit.actions.advanceRevisionRequested";
    if (kind === "salary") return "audit.actions.advanceSalaryConfirmed";
    if (kind === "employee") return "audit.actions.advanceAccountableConfirmed";
    return undefined;
  }
  return undefined;
}

export default function RequestAuditTimeline({ requestType, requestId, kind }: Props) {
  const { t } = useTranslation("EmployeePortal");
  const locale = useLocale();
  const { data, isLoading } = useGetRequestAuditQuery(
    { requestType, requestId },
    { refetchOnMountOrArgChange: true },
  );
  const { scrollRef, hasScroll } = useScrollDetection();

  const config = useMemo<AuditEntryItemConfig>(
    () => ({
      actionLabelMap: REQUEST_AUDIT_ACTION_LABEL_MAP,
      iconMap: REQUEST_AUDIT_ICON_MAP,
      filteredChangeKeys: [
        "review_action",
        "snapshot",
        "status",
        "effective_date",
        "new_values_json",
        "revision_count",
        "application_review_note",
      ],
      getActionLabelKey(entry) {
        return getSigningStatusLabelKey(entry) ?? getAdvanceLabelKey(entry, kind);
      },
      getEntryNote,
    }),
    [kind],
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        <h5 className="text-body-bold-md content-base-primary">{t("audit.title")}</h5>
        <AuditTimelineSkeleton />
      </div>
    );
  }

  const entries = (data?.results ?? []).filter((entry) => entry.action !== "advance.approved");

  return (
    <div className="flex flex-col gap-3 min-h-0 flex-1">
      <h5 className="text-body-bold-md content-base-primary shrink-0">{t("audit.title")}</h5>

      {entries.length === 0 ? (
        <p className="text-body-regular-sm content-base-secondary">{t("audit.empty")}</p>
      ) : (
        <div
          ref={scrollRef}
          className={cn("flex-1 min-h-0 overflow-auto page-scroll", hasScroll && "pr-3")}>
          {entries.map((entry, index) => (
            <AuditEntryItem
              key={entry.id}
              entry={entry}
              isLast={index === entries.length - 1}
              namespace="audit"
              config={config}
              t={t}
              locale={locale}
            />
          ))}
        </div>
      )}
    </div>
  );
}
